import { getAccessTokenForApi } from "@/lib/auth/tokenProvider";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

const buildAuthHeaders = async (extra = {}) => {
  const token = await getAccessTokenForApi();
  const headers = { ...extra };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (typeof window !== "undefined") {
    const impersonated = localStorage.getItem("impersonated_user_email");
    if (impersonated) {
      headers["X-Impersonate-User"] = impersonated;
    }
  }
  return headers;
};

const createError = async (response) => {
  let parsed;
  const text = await response.text();
  try {
    parsed = text ? JSON.parse(text) : { error: response.statusText };
  } catch {
    parsed = { error: text || response.statusText };
  }

  const error = new Error(parsed?.error || `Request failed with status ${response.status}`);
  error.status = response.status;
  error.response = {
    status: response.status,
    data: parsed,
  };
  return error;
};

const apiRequest = async (path, options = {}) => {
  const url = `${API_BASE}${path}`;
  const response = await fetch(url, options);
  if (!response.ok) {
    throw await createError(response);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return await response.json();
  }
  return await response.text();
};

const toEntityApi = (entityName) => ({
  async list(orderBy = "created_date", limit = null) {
    const params = new URLSearchParams();
    if (orderBy) params.set("orderBy", orderBy);
    if (limit) params.set("limit", String(limit));
    const result = await apiRequest(`/api/entities/${entityName}?${params.toString()}`, {
      method: "GET",
      headers: await buildAuthHeaders(),
    });
    return result.data || [];
  },

  async filter(conditions = {}, orderBy = "created_date", limit = null) {
    const result = await apiRequest(`/api/entities/${entityName}/filter`, {
      method: "POST",
      headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ conditions, orderBy, limit }),
    });
    return result.data || [];
  },

  async get(id) {
    const result = await apiRequest(`/api/entities/${entityName}/${id}`, {
      method: "GET",
      headers: await buildAuthHeaders(),
    });
    return result.data || null;
  },

  async create(data) {
    const result = await apiRequest(`/api/entities/${entityName}`, {
      method: "POST",
      headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(data),
    });
    return result.data;
  },

  async update(id, data) {
    const result = await apiRequest(`/api/entities/${entityName}/${id}`, {
      method: "PATCH",
      headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(data),
    });
    return result.data;
  },

  async delete(id) {
    const result = await apiRequest(`/api/entities/${entityName}/${id}`, {
      method: "DELETE",
      headers: await buildAuthHeaders(),
    });
    return result.data;
  },
});

const hasFilePayload = (payload) => {
  if (!payload || typeof payload !== "object") return false;
  const fileCtor = typeof File !== "undefined" ? File : null;
  const blobCtor = typeof Blob !== "undefined" ? Blob : null;
  return Object.values(payload).some((value) => {
    if (fileCtor && value instanceof fileCtor) return true;
    if (blobCtor && value instanceof blobCtor) return true;
    return false;
  });
};

const invokeFunction = async (name, payload = {}) => {
  let body;
  let headers = await buildAuthHeaders();

  if (hasFilePayload(payload)) {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (typeof Blob !== "undefined" && value instanceof Blob) {
        formData.append(key, value);
      } else {
        formData.append(key, String(value));
      }
    });
    body = formData;
  } else {
    headers = await buildAuthHeaders({ "Content-Type": "application/json" });
    body = JSON.stringify(payload || {});
  }

  const result = await apiRequest(`/api/functions/${name}`, {
    method: "POST",
    headers,
    body,
  });

  return { data: result };
};

const entityProxy = new Proxy({}, {
  get(_target, prop) {
    if (typeof prop !== "string") return undefined;
    return toEntityApi(prop);
  },
});

const authApi = {
  async me() {
    return await apiRequest("/api/auth/me", {
      method: "GET",
      headers: await buildAuthHeaders(),
    });
  },

  logout(returnTo) {
    if (returnTo && typeof window !== "undefined") {
      window.location.href = returnTo;
    }
    return { success: true };
  },

  redirectToLogin(returnTo) {
    console.warn(
      "base44.auth.redirectToLogin is deprecated. Use AuthContext.navigateToLogin (Auth0 SDK) instead.",
      { returnTo }
    );
    return { returnTo };
  },
};

const usersApi = {
  async inviteUser(email, role = "user") {
    const result = await apiRequest("/api/users/invite", {
      method: "POST",
      headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ email, role }),
    });
    return result.data;
  },
};

const appLogsApi = {
  async logUserInApp(page) {
    const result = await apiRequest("/api/app-logs/page-view", {
      method: "POST",
      headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ page }),
    });
    return result.data;
  },
};

const integrationsApi = {
  Core: {
    async InvokeLLM(payload) {
      return await apiRequest("/api/integrations/core/invoke-llm", {
        method: "POST",
        headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload || {}),
      });
    },

    async SendEmail(payload) {
      return await apiRequest("/api/integrations/core/send-email", {
        method: "POST",
        headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload || {}),
      });
    },

    async UploadFile({ file }) {
      const formData = new FormData();
      formData.append("file", file);
      return await apiRequest("/api/integrations/core/upload-file", {
        method: "POST",
        headers: await buildAuthHeaders(),
        body: formData,
      });
    },

    async ExtractDataFromUploadedFile(payload) {
      return await apiRequest("/api/integrations/core/extract-data", {
        method: "POST",
        headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload || {}),
      });
    },
  },
};

export const base44 = {
  entities: entityProxy,
  auth: authApi,
  functions: {
    invoke: invokeFunction,
  },
  integrations: integrationsApi,
  users: usersApi,
  appLogs: appLogsApi,
};
